import React, { useState } from "react";
import { Plus, Search, Eye, CheckCircle, Trash2, FileText, IndianRupee, Download, Layers, X, Receipt } from "lucide-react";
import { usePayslips } from "../hooks/usePayslips";
import { GeneratePayslipModal } from "../components/payslips/GeneratePayslipModal";
import { SendPayslipModal } from "../components/payslips/SendPayslipModal";
import { PayslipPreview } from "../components/payslips/PayslipPreview";
import { TransactionDetailsModal } from "../components/payslips/TransactionDetailsModal";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import StatCard from "@/components/ui/StatCard";
import { MONTHS, MONTH_OPTIONS, PAYSLIP_STATUS_COLORS, PAYSLIP_STATUS_LABELS } from "../constants/hrConstants";
import { useHR } from "../context/HRContext";
import toast from "react-hot-toast";

export function PayslipsScreen() {
  const { paySetups } = useHR();
  const {
    payslips, allPayslips, stats, employees, yearOptions,
    filterEmp, setFilterEmp, filterMonth, setFilterMonth,
    filterYear, setFilterYear, filterStatus, setFilterStatus,
    isGenerateOpen, previewPayslip, setPreviewPayslip, isSaving,
    formData, formErrors,
    openGenerate, closeGenerate, updateField, save,
    bulkGeneratePayslips,
    markPaid, deletePayslip,
  } = usePayslips();

  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [selectedTransactionPayslip, setSelectedTransactionPayslip] = useState(null);

  const handleOpenBatchModal = () => {
    setIsBatchModalOpen(true);
  };

  const handleSave = async () => {
    const ok = await save();
    if (ok) toast.success("Payslip generated successfully!");
  };

  const handleMarkPaid = async (id) => {
    await markPaid(id);
    toast.success("Marked as paid");
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
  };

  const executeDelete = () => {
    if (deleteTargetId) {
      deletePayslip(deleteTargetId);
      toast.success("Payslip deleted");
      setDeleteTargetId(null);
    }
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-400 bg-slate-50 min-h-screen pb-10">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard variant="luxury" title="Total Payslips" value={stats.total} topColor="border-t-blue-500" iconHoverColor="group-hover:text-blue-500" illustration={<FileText className="w-10 h-10" strokeWidth={1.5} />}/>
        <StatCard variant="luxury" title="Generated" value={stats.generated} topColor="border-t-purple-500" iconHoverColor="group-hover:text-purple-500" illustration={<Download className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="Paid" value={stats.paid} topColor="border-t-emerald-500" iconHoverColor="group-hover:text-emerald-500" illustration={<CheckCircle className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="Total Net Disbursed" value={`₹${stats.totalNetPay.toLocaleString("en-IN")}`} topColor="border-t-blue-500" iconHoverColor="group-hover:text-blue-500" illustration={<IndianRupee className="w-10 h-10" strokeWidth={1.5} />} sub="Paid payslips" />
      </div>

      {/* Control Panel */}
      <div className="card p-2 sm:p-3 flex flex-wrap items-center gap-2 bg-white shadow-sm border border-slate-100 animate-in fade-in slide-in-from-top-1 rounded-md">

        {/* Search Toggle */}
        <button
          onClick={() => { setSearchVisible(v => !v); if (searchVisible) setFilterEmp(''); }}
          className={`p-2 h-9 w-9 flex items-center justify-center rounded-md border transition-all ${
            searchVisible
              ? 'bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300'
          }`}
          title="Search Employee"
        >
          <Search size={16} />
        </button>

        {/* Month Filter */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[110px] w-auto"
            value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
          >
            <option value="">All Months</option>
            {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        {/* Year Filter */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden sm:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[90px] w-auto"
            value={filterYear} onChange={(e) => setFilterYear(e.target.value)}
          >
            <option value="">All Years</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Status Filter */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden sm:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[110px] w-auto"
            value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="generated">Generated</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={handleOpenBatchModal}
            className="h-9 flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300 hover:scale-105 active:scale-95 transition-all"
            title="Send Emails"
          >
            <Layers className="w-4 h-4" /> <span className="hidden sm:inline">Send Emails</span>
          </button>
          <button
            onClick={() => openGenerate()}
            className="h-9 flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 hover:scale-105 active:scale-95 transition-all"
          >
            <Plus className="w-4 h-4" /> Generate
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      {searchVisible && (
        <div className="card p-3 flex items-center gap-3 bg-white shadow-sm border border-slate-100 rounded-md animate-in fade-in slide-in-from-top-1">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            type="text"
            autoFocus
            className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            placeholder="Search employee..."
            value={filterEmp}
            onChange={(e) => setFilterEmp(e.target.value)}
          />
          {filterEmp && (
            <button onClick={() => setFilterEmp('')} className="text-slate-400 hover:text-slate-600">
              <X size={15} />
            </button>
          )}
        </div>
      )}

      {/* Payslips Table */}
      {payslips.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-14 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium text-[13px]">No payslips found</p>
          <p className="text-slate-400 text-[12px] mt-1">Generate payslips for your employees</p>
          <button onClick={openGenerate} className="btn-primary mt-4 mx-auto flex items-center gap-2">
            <Plus className="w-4 h-4" /> Generate Payslip
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto min-w-[1050px]">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {[
                    { label: "EMP_ID", className: "w-[8%] min-w-[80px]" },
                    { label: "Employee Name", className: "w-[15%] min-w-[140px]" },
                    { label: "Payslip ID", className: "w-[8%] min-w-[80px]" },
                    { label: "Period", className: "w-[10%] min-w-[100px]" },
                    { label: "Working Days", className: "w-[10%] min-w-[105px] text-center" },
                    { label: "Gross Earnings", className: "w-[11%] min-w-[110px]" },
                    { label: "Deductions", className: "w-[9%] min-w-[90px]" },
                    { label: "Net Pay", className: "w-[10%] min-w-[100px]" },
                    { label: "Status", className: "w-[9%] min-w-[90px]" },
                    { label: "Generated", className: "w-[10%] min-w-[100px]" },
                    { label: "Actions", className: "w-[10%] min-w-[110px] text-right" }
                  ].map((h) => (
                    <th 
                      key={h.label} 
                      className={`px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap ${h.className}`}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {payslips.map((p, idx) => (
                  <tr key={p.id} className="hover:bg-slate-50/50 transition-colors group">
                    {/* EMP_ID */}
                    <td className="px-6 py-4 text-xs font-mono font-bold text-slate-700 truncate">#{p.employeeId}</td>
                    
                    {/* Employee Name */}
                    <td className="px-6 py-4 text-sm font-bold text-slate-800 truncate" title={p.employeeName}>
                      {p.employeeName}
                    </td>
                    
                    {/* Payslip ID */}
                    <td className="px-6 py-4 text-xs font-mono font-semibold text-slate-400 truncate">#{p.id.slice(0,6)}</td>
                    
                    {/* Period */}
                    <td className="px-6 py-4 text-sm font-semibold text-slate-700 truncate">
                      {MONTHS[Number(p.month) - 1]} {p.year}
                    </td>
                    
                    {/* Working Days */}
                    <td className="px-6 py-4 text-center truncate">
                      <span className="text-sm font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md">{p.presentDays}/{p.workingDays}</span>
                    </td>
                    
                    {/* Earnings / Deductions / Net */}
                    <td className="px-6 py-4 text-sm font-semibold text-slate-700 truncate">
                      ₹{Number(p.grossEarnings).toLocaleString("en-IN")}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-red-500 truncate">
                      -₹{Number(p.totalDeductions).toLocaleString("en-IN")}
                    </td>
                    <td className="px-6 py-4 text-base font-black text-blue-600 truncate">
                      ₹{Number(p.netPay).toLocaleString("en-IN")}
                    </td>
                    
                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${PAYSLIP_STATUS_COLORS[p.status]}`}>
                        {PAYSLIP_STATUS_LABELS[p.status]}
                      </span>
                    </td>
                    
                    {/* Generated On */}
                    <td className="px-6 py-4 text-xs font-medium text-slate-500 truncate">
                      {new Date(p.generatedOn).toLocaleDateString("en-IN")}
                    </td>
                    
                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setPreviewPayslip(p)}
                          className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-blue-600 hover:bg-blue-50 hover:border-blue-200 transition-colors"
                          title="View / Download Payslip"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setSelectedTransactionPayslip(p)}
                          className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                          title="Transaction & Bank Details"
                        >
                          <Receipt className="w-4 h-4" />
                        </button>
                        {p.status === "generated" && (
                          <button
                            onClick={() => handleMarkPaid(p.id)}
                            className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                            title="Mark as Paid"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>

      {/* Modals */}
      <GeneratePayslipModal
        isOpen={isGenerateOpen}
        onClose={closeGenerate}
        employees={employees}
        paySetups={paySetups}
        formData={formData}
        formErrors={formErrors}
        onFieldChange={updateField}
        onSubmit={handleSave}
        isSaving={isSaving}
        yearOptions={yearOptions}
      />
      <PayslipPreview
        payslip={previewPayslip}
        onClose={() => setPreviewPayslip(null)}
      />
      <TransactionDetailsModal
        isOpen={!!selectedTransactionPayslip}
        onClose={() => setSelectedTransactionPayslip(null)}
        payslip={selectedTransactionPayslip}
      />
      <DeleteConfirmModal
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={executeDelete}
        title="Delete Payslip"
        message="Are you sure you want to delete this payslip? This action cannot be undone."
      />

      <SendPayslipModal 
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        month={filterMonth}
        year={filterYear}
        onGenerate={() => {}}
      />
    </>
  );
}
